/**
 * Community Helper Utilities
 * Utility functions for community features including time formatting,
 * image URL handling, and data transformations
 */
import DOMPurify from "dompurify";

/**
 * Format timestamp to relative time (e.g., "2 hours ago")
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} - Formatted relative time
 */
export const formatTimeAgo = (timestamp) => {
  try {
    if (!timestamp) return "Just now";

    const now = new Date();
    const date = new Date(timestamp);

    // Check if date is valid
    if (isNaN(date.getTime())) return "Just now";

    const seconds = Math.floor((now - date) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) return "Just now";
    if (minutes < 60)
      return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
    if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
    if (days < 7) return `${days} ${days === 1 ? "day" : "days"} ago`;
    if (days < 28) return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
    if (days < 365)
      return `${months || 1} ${months <= 1 ? "month" : "months"} ago`;
    return `${years} ${years === 1 ? "year" : "years"} ago`;
  } catch (error) {
    console.error("Error formatting time:", error);
    return "Just now";
  }
};

/**
 * Get full image URL from path
 * Converts relative paths to absolute URLs
 * @param {string} imagePath - Relative or absolute image path
 * @returns {string} - Full image URL
 */
export const getFullImageUrl = (imagePath) => {
  try {
    if (!imagePath) {
      console.log("[getFullImageUrl] No image path provided");
      return null;
    }

    // If already a full URL, return as is
    if (imagePath.startsWith("http://") || imagePath.startsWith("https://")) {
      console.log("[getFullImageUrl] Already full URL:", imagePath);
      return imagePath;
    }

    // If it's a data URL or blob URL, return as is
    if (imagePath.startsWith("data:") || imagePath.startsWith("blob:")) {
      console.log(
        "[getFullImageUrl] Data/Blob URL:",
        imagePath.substring(0, 50) + "...",
      );
      return imagePath;
    }

    // Get base URL from environment or use default
    const baseURL =
      process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:1111";

    // Remove leading slash if present to avoid double slashes
    const cleanPath = imagePath.startsWith("/")
      ? imagePath.substring(1)
      : imagePath;

    const fullUrl = `${baseURL}/${cleanPath}`;

    return fullUrl;
  } catch (error) {
    console.error("[getFullImageUrl] Error:", error, "Input:", imagePath);
    return null;
  }
};

/**
 * Generate avatar URL using DiceBear API
 * Fallback for users without profile images
 * @param {string} seed - Seed for avatar generation (usually username)
 * @param {string} [style='avataaars'] - Avatar style
 * @returns {string} - Avatar URL
 */
export const generateAvatarUrl = (seed, style = "avataaars") => {
  try {
    if (!seed) return null;

    // Clean seed for URL usage
    const cleanSeed = encodeURIComponent(seed.trim());

    // Random background colors for variety
    const bgColors = ["b6e3f4", "c0aede", "d1d4f9", "ffd5dc", "ffdfbf"];
    const randomBg = bgColors[Math.floor(Math.random() * bgColors.length)];

    return `https://api.dicebear.com/7.x/${style}/svg?seed=${cleanSeed}&backgroundColor=${randomBg}&clothingColor=374151`;
  } catch (error) {
    console.error("Error generating avatar URL:", error);
    return null;
  }
};

/**
 * Truncate text to specified length with ellipsis
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength = 100) => {
  try {
    if (!text || text.length <= maxLength) return text || "";
    return text.substring(0, maxLength).trim() + "...";
  } catch (error) {
    console.error("Error truncating text:", error);
    return text || "";
  }
};

// Short-form abbreviations for nursing roles, used on chip filters and
// the post-card author meta line (e.g. "Registered Nurses" -> "RN").
export const PROFESSION_SHORT_LABELS = {
  "Registered Nurses": "RN",
  "Licensed Practical and Licensed Vocational Nurses": "LPN/LVN",
  "Nursing Assistants": "CNA",
  "Home Health and Personal Care Aides": "HHA",
  "Nursing Instructors and Teachers Postsecondary": "Instructor",
  "Nurse Midwives": "Midwife",
  "Nurse Practitioners": "NP",
  "Nurse Anesthetists": "CRNA",
};

// Short-form abbreviations for specialties (e.g. "Medical-Surgical" -> "Med-Surg").
export const SPECIALTY_SHORT_LABELS = {
  "Emergency Room (ER)": "ER",
  "Operating Room (OR)": "OR",
  "Cardiac Care Unit (CCU)": "CCU",
  "Medical-Surgical": "Med-Surg",
  "Case Management": "Case Mgmt",
  "Wound Care": "Wound",
};

export const shortRole = (name) =>
  (name && PROFESSION_SHORT_LABELS[name]) || name || "";

export const shortSpecialty = (name) =>
  (name && SPECIALTY_SHORT_LABELS[name]) || name || "";

// Per-category background colors used on category chips/badges.
// Mirrors the `colour` field returned by /get-all-categories so any UI
// that can't read that field directly still gets a consistent palette.
export const CATEGORY_COLORS = {
  "Hospital Review": "#DBEAFE",
  "Debt and Finance": "#F3E8FF",
  "Career Discussion": "#FED7AA",
  News: "#CFFAFE",
  "Salary Discussion": "#FCE7F3",
  "Job Listings": "#FEF9C3",
  Announcements: "#FECACA",
};

// Fallback background when a category isn't in the map.
const CATEGORY_COLOR_FALLBACK = "#E5E7EB";

export const categoryBg = (name) =>
  (name && CATEGORY_COLORS[name]) || CATEGORY_COLOR_FALLBACK;

/**
 * Format number with K, M suffixes for large numbers
 * @param {number} num - Number to format
 * @returns {string} - Formatted number string
 */
export const formatCount = (num) => {
  try {
    if (!num || num === 0) return "0";
    if (num < 1000) return num.toString();
    if (num < 1000000) return `${(num / 1000).toFixed(1)}K`;
    return `${(num / 1000000).toFixed(1)}M`;
  } catch (error) {
    console.error("Error formatting count:", error);
    return "0";
  }
};

/**
 * Check if edit is still allowed (within 5 minutes)
 * @param {string|Date} createdAt - Comment creation timestamp
 * @returns {boolean} - True if edit is allowed
 */
export const canEditComment = (createdAt) => {
  try {
    if (!createdAt) return false;

    const now = new Date();
    const created = new Date(createdAt);

    if (isNaN(created.getTime())) return false;

    const diffMinutes = (now - created) / (1000 * 60);
    return diffMinutes <= 5;
  } catch (error) {
    console.error("Error checking edit permission:", error);
    return false;
  }
};

/**
 * Validate image file
 * @param {File} file - Image file to validate
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateImageFile = (file) => {
  try {
    if (!file) {
      return { valid: false, error: "No file selected" };
    }

    // Check file type by MIME type or extension
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/heic",
      "image/heif",
    ];
    const fileName = file.name.toLowerCase();
    const isValidExtension = fileName.endsWith('.jpg') || 
                            fileName.endsWith('.jpeg') || 
                            fileName.endsWith('.png') || 
                            fileName.endsWith('.heic') || 
                            fileName.endsWith('.heif');
    
    if (!validTypes.includes(file.type.toLowerCase()) && !isValidExtension) {
      return {
        valid: false,
        error: "Invalid file type. Only JPEG, PNG, HEIC are allowed.",
      };
    }

    // Check file size (max 2MB)
    const maxSize = 2 * 1024 * 1024; // 2MB in bytes
    if (file.size > maxSize) {
      return { valid: false, error: "File size must be less than 2MB" };
    }

    return { valid: true, error: null };
  } catch (error) {
    console.error("Error validating image file:", error);
    return { valid: false, error: "Error validating file" };
  }
};

/**
 * Create FormData for post creation
 * @param {Object} postData - Post data
 * @param {string} postData.content - Post content
 * @param {number} postData.category - Category ID
 * @param {boolean} postData.isAnonymous - Post anonymously flag
 * @param {File[]} postData.images - Image files
 * @returns {FormData} - FormData object ready for API
 */
// export const createPostFormData = (postData) => {
//   try {
//     const formData = new FormData();

//     formData.append('content', postData.content || '');
//     formData.append('category', postData.category || '');
//     formData.append('is_post_anonymously', postData.isAnonymous ? '1' : '0');

//     // Append images if present
//     if (postData.images && postData.images.length > 0) {
//       postData.images.forEach((image) => {
//         formData.append('post_images', image);
//       });
//     }

//     return formData;
//   } catch (error) {
//     console.error('Error creating post FormData:', error);
//     throw error;
//   }
// };
export const createPostFormData = (postData) => {
  try {
    const formData = new FormData();
    formData.append("title", postData.title || "");
    formData.append("content", postData.content || "");
    formData.append("category", postData.category || "");
    formData.append("is_post_anonymously", postData.isAnonymous ? "1" : "0");

    // Append images
    if (postData.images && postData.images.length > 0) {
      postData.images.forEach((image) => {
        formData.append("post_images", image);
      });
    }

    if (postData.link_preview) {
      formData.append(
        "link_preview",
        JSON.stringify({
          title: postData.link_preview.title || "",
          description: postData.link_preview.description || "",
          image: postData.link_preview.image || "",
          domain: postData.link_preview.domain || "",
        }),
      );
    }

    return formData;
  } catch (error) {
    console.error("Error creating post FormData:", error);
    throw error;
  }
};

/**
 * Create FormData for comment creation
 * @param {Object} commentData - Comment data
 * @param {number} commentData.postId - Post ID
 * @param {string} [commentData.content] - Optional Comment content
 * @param {File} [commentData.image] - Optional comment image
 * @param {number} [commentData.parentId] - Optional parent comment ID for replies
 * @returns {FormData} - FormData object ready for API
 */
export const createCommentFormData = (commentData) => {
  try {
    const formData = new FormData();

    formData.append("post_id", commentData.postId || "");
    // formData.append('content', commentData.content || '');

    if (commentData.content && commentData.content.trim().length > 0) {
      formData.append("content", commentData.content.trim());
    }

    if (commentData.parentId) {
      formData.append("parent_id", commentData.parentId);
    }

    if (commentData.image) {
      formData.append("image_path", commentData.image);
    }

    // Add link preview data if available
    if (commentData.link_preview) {
      formData.append("link_preview", JSON.stringify(commentData.link_preview));
    }

    return formData;
  } catch (error) {
    console.error("Error creating comment FormData:", error);
    throw error;
  }
};

/**
 * Create FormData for comment editing
 * @param {Object} editData - Edit data
 * @param {number} editData.id - Comment ID
 * @param {string} editData.content - Updated content
 * @param {File} [editData.image] - Optional updated image
 * @returns {FormData} - FormData object ready for API
 */
export const createCommentEditFormData = (editData) => {
  try {
    const formData = new FormData();

    formData.append("id", editData.id || "");
    formData.append("content", editData.content || "");

    if (editData.image) {
      formData.append("image_path", editData.image);
    }

    return formData;
  } catch (error) {
    console.error("Error creating comment edit FormData:", error);
    throw error;
  }
};

/**
 * Safe JSON parse with fallback
 * @param {string} jsonString - JSON string to parse
 * @param {*} fallback - Fallback value if parsing fails
 * @returns {*} - Parsed value or fallback
 */
export const safeJsonParse = (jsonString, fallback = null) => {
  try {
    return jsonString ? JSON.parse(jsonString) : fallback;
  } catch (error) {
    console.error("Error parsing JSON:", error);
    return fallback;
  }
};

/**
 * Parse text and identify @mentions
 * @param {string} text - Text to parse
 * @returns {Array} - Array of text parts with mention flags
 */
export const parseMentions = (text) => {
  if (!text) return [];

  // Match @mentions holding either a username or a full name.
  // - @username, @user.name, @user_name
  // - @Full Name (single spaces allowed between name words)
  // Mentions are inserted with a trailing double space as a delimiter, so a
  // name group only extends across single spaces and stops at the double space,
  // punctuation, a newline, or the end of the string.
  const mentionRegex = /@([\w.]+(?: [\w.]+)*)(?=\s{2,}|[,!?;:]|\n|$)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  mentionRegex.lastIndex = 0;

  while ((match = mentionRegex.exec(text)) !== null) {
    const matchedMention = match[0].trim(); // Full match including @ and trimmed
    const username = match[1].trim(); // Username without @ and trimmed
    const startIndex = match.index;

    // Add text before the mention
    if (startIndex > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, startIndex),
      });
    }

    // Add the mention
    parts.push({
      type: "mention",
      content: `@${username}`, // Ensure @ is included
      username: username,
    });

    lastIndex = startIndex + matchedMention.length;
  }

  // Add remaining text after last mention
  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  // If no mentions found, return the whole text as one part
  if (parts.length === 0) {
    parts.push({
      type: "text",
      content: text,
    });
  }

  return parts;
};

/**
 * Parse text for both links and mentions
 * @param {string} text - Text to parse
 * @returns {Array} - Array of text parts with type flags
 */
export const parseTextWithLinksAndMentions = (text) => {
  if (!text) return [];

  // Step 1: Parse emails first and mark them
  let parts = [{ type: "text", content: text }];

  // Parse emails
  const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
  const newParts = [];
  
  parts.forEach(part => {
    if (part.type === "text") {
      const content = part.content;
      let lastIndex = 0;
      let match;
      emailRegex.lastIndex = 0;

      while ((match = emailRegex.exec(content)) !== null) {
        // Add text before email
        if (match.index > lastIndex) {
          newParts.push({
            type: "text",
            content: content.substring(lastIndex, match.index)
          });
        }
        
        // Add email
        newParts.push({
          type: "email",
          content: match[0],
          email: match[0]
        });
        
        lastIndex = emailRegex.lastIndex;
      }
      
      // Add remaining text
      if (lastIndex < content.length) {
        newParts.push({
          type: "text",
          content: content.substring(lastIndex)
        });
      }
    } else {
      newParts.push(part);
    }
  });

  parts = newParts.filter(part => part.content.length > 0);

  // Step 2: Parse links from remaining text parts
  const linkParts = [];
  const linkRegex = /((https?:\/\/)?(localhost:\d+|(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,})(\/[-a-zA-Z0-9()@:%_+.~#?&/=]*)?)/gi;
  
  parts.forEach(part => {
    if (part.type === "text") {
      const content = part.content;
      let lastIndex = 0;
      let match;
      linkRegex.lastIndex = 0;

      while ((match = linkRegex.exec(content)) !== null) {
        const matchedUrl = match[0];
        
        // Skip if this looks like it contains an @ (already processed as email)
        if (matchedUrl.includes("@")) {
          continue;
        }

        // Add text before link
        if (match.index > lastIndex) {
          linkParts.push({
            type: "text",
            content: content.substring(lastIndex, match.index)
          });
        }
        
        // Add link
        let href = matchedUrl;
        if (!href.startsWith("http")) {
          href = `https://${href}`;
        }
        
        linkParts.push({
          type: "link",
          content: matchedUrl,
          href: href
        });
        
        lastIndex = linkRegex.lastIndex;
      }
      
      // Add remaining text
      if (lastIndex < content.length) {
        linkParts.push({
          type: "text",
          content: content.substring(lastIndex)
        });
      }
    } else {
      linkParts.push(part);
    }
  });

  parts = linkParts.filter(part => part.content.length > 0);

  // Step 3: Parse mentions from remaining text parts
  const finalParts = [];
  // Match @mentions holding either a username or a full name.
  // - @username, @user.name, @user_name
  // - @Full Name (single spaces allowed between name words)
  // Mentions are inserted with a trailing double space as a delimiter, so a
  // name group only extends across single spaces and stops at the double space,
  // punctuation, a newline, or the end of the string.
  const mentionRegex = /@([\w.]+(?: [\w.]+)*)(?=\s{2,}|[,!?;:]|\n|$)/g;
  
  parts.forEach(part => {
    if (part.type === "text") {
      const content = part.content;
      let lastIndex = 0;
      let match;
      mentionRegex.lastIndex = 0;

      while ((match = mentionRegex.exec(content)) !== null) {
        // Add text before mention
        if (match.index > lastIndex) {
          finalParts.push({
            type: "text",
            content: content.substring(lastIndex, match.index)
          });
        }
        
        // Add mention
        finalParts.push({
          type: "mention",
          content: match[0].trim(),
          username: match[1].trim()
        });
        
        lastIndex = mentionRegex.lastIndex;
      }
      
      // Add remaining text
      if (lastIndex < content.length) {
        finalParts.push({
          type: "text",
          content: content.substring(lastIndex)
        });
      }
    } else {
      finalParts.push(part);
    }
  });

  return finalParts.filter(part => part.content && part.content.length > 0);
};

/**
 * Linkify text - converts URLs to clickable links
 * Detects URLs in text and wraps them in anchor tags
 * @param {string} text - Text to linkify
 * @returns {JSX.Element[]} - Array of text and link elements
 */
export const linkifyText = (text) => {
  if (!text) return [];

  // Comprehensive URL regex that matches:
  // - http:// and https:// URLs (with everything after including paths, query params, etc.)
  // - www. URLs
  // - URLs without protocol (e.g., google.com, abc.com)
  // Updated to handle URLs with paths, query params, fragments, etc.
  // const urlRegex = /(?:(?:https?:\/\/)|(?:www\.))?(?=[^ ]*[a-zA-Z])[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/gi;
  const urlRegex = /((https?:\/\/)?(localhost:\d+|(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z]{2,})(\/[-a-zA-Z0-9()@:%_+.~#?&/=]*)?)/gi;

  // const urlRegex = /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_\+.~#?&\/=]*)/gi;

  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  urlRegex.lastIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const matchedUrl = match[0];
    const startIndex = match.index;

    // Skip if this looks like an email address (contains @ before the domain)
    if (matchedUrl.includes("@") && !matchedUrl.startsWith("http")) {
      continue;
    }

    // Add text before the URL
    if (startIndex > lastIndex) {
      parts.push({
        type: "text",
        content: text.substring(lastIndex, startIndex),
      });
    }

    // Determine the href (add protocol if missing)
    let href = matchedUrl;
    if (!matchedUrl.match(/^https?:\/\//i)) {
      href = `https://${matchedUrl}`;
    }

    // Add the link
    parts.push({
      type: "link",
      content: matchedUrl,
      href: href,
    });

    lastIndex = urlRegex.lastIndex;
  }

  // Add remaining text after last URL
  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  // If no URLs found, return text as is
  if (parts.length === 0) {
    parts.push({
      type: "text",
      content: text,
    });
  }

  return parts;
};

/**
 * Replace non-breaking spaces (&nbsp; / U+00A0) with normal breakable spaces.
 * The Quill editor used for post composition (and some keyboards/OS-level
 * predictive text) can serialize every regular space as a non-breaking one,
 * which prevents the browser from ever wrapping at a word boundary — long
 * sentences then get forced to break mid-word at whatever character happens
 * to hit the container edge. Word separators in post/comment content are
 * never intentionally non-breaking, so this normalization is always safe.
 * @param {string} html - Raw HTML (or plain text) content
 * @returns {string} - Same content with nbsp characters/entities normalized
 */
export const normalizeNbsp = (html) =>
  (html || "").replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " ");

/**
 * Sanitize rich-text post/comment content before rendering with html-react-parser.
 * Strips `style`/`class` attributes so pasted content (e.g. from Word/Google Docs,
 * which embeds inline font-family/font-size overrides) can't override the app's
 * own typography, while still allowing safe tags like <p>, <b>, <a>, <br>, etc.
 * Also normalizes non-breaking spaces so long sentences wrap normally instead
 * of breaking mid-word (see `normalizeNbsp`).
 * @param {string} html - Raw HTML content from the API
 * @returns {string} - Sanitized HTML safe to pass to html-react-parser
 */
export const sanitizeRichContent = (html) =>
  normalizeNbsp(
    DOMPurify.sanitize(html || "", { FORBID_ATTR: ["style", "class"] }),
  );

// Typographic formatting tags stripped from feed/sidebar previews — DOMPurify
// removes the tag itself but keeps its text content (it's not a
// content-bearing tag like <script>), so "<b>bold</b> text" just renders as
// "bold text". Lists (<ul>/<ol>/<li>) are intentionally NOT in this list —
// previews should keep bullet/numbered structure, only "typing style"
// formatting is removed.
const PREVIEW_FORBID_TAGS = [
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ins",
  "s",
  "strike",
  "del",
  "mark",
  "sub",
  "sup",
  "small",
  "big",
  "font",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
];

/**
 * Sanitize post content for compact feed/sidebar previews (PostCard,
 * RightSidebar): strips bold/italic/underline/heading formatting so
 * previews render as plain text, while still rendering bullet and
 * numbered lists as actual lists, and keeping links clickable.
 * The full post detail view (PostDetailModal/ArticleDetailModal) should
 * keep using `sanitizeRichContent` instead, to show formatting exactly as
 * the post was authored.
 * @param {string} html - Raw HTML content from the API
 * @returns {string} - Sanitized HTML safe to pass to html-react-parser
 */
export const sanitizePreviewContent = (html) =>
  normalizeNbsp(
    DOMPurify.sanitize(html || "", {
      FORBID_ATTR: ["style", "class"],
      FORBID_TAGS: PREVIEW_FORBID_TAGS,
    }),
  );

/**
 * Strip tags/attributes from HTML content, returning just the visible text.
 * Use this before truncating an HTML string to a fixed length — slicing raw
 * HTML by character index can cut a tag or entity (e.g. `&nbsp;`) in half,
 * leaving mangled fragments like a literal "&nb" in the rendered output.
 * @param {string} html - Raw HTML (or plain text) content
 * @returns {string} - The rendered text only, no tags/entities
 */
export const htmlToPlainText = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") return html;
  const div = document.createElement("div");
  div.innerHTML = sanitizeRichContent(html);
  return div.textContent || div.innerText || "";
};

/**
 * Get the visible text length of HTML content, ignoring tags/attributes.
 * Use this (instead of `content.length`) for "is this long enough to
 * truncate/show Read more" checks — raw HTML length is misleading because
 * pasted content can carry large inline style attributes that inflate the
 * string length without adding any visible text.
 * @param {string} html - Raw HTML (or plain text) content
 * @returns {number} - Length of the rendered text only
 */
export const getVisibleTextLength = (html) => htmlToPlainText(html).length;
